// Plans: what each one makes, what they share, and the way back to the
// customer's brief once one is chosen.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  AudioLines,
  Check,
  ChevronDown,
  Clapperboard,
  Download,
  Feather,
  FileText,
  LoaderCircle,
  Lock,
  Music2,
  Plus,
} from "lucide-react";
import { post } from "./api";
import { Button, LanguageSwitch, Logo, Mark, Modal } from "./components";
import { savedBrief } from "./Creation";
import {
  creditNumber,
  PLAN_IDS,
  PLANS,
  TOPUP,
  videosFor,
  type PlanId,
} from "../shared/catalog";
import type { Account, DirectorModel, StudioConfig } from "../shared/types";
import { bilingual, lang, locale, text, useLang, useText } from "./i18n";
import { watchPage } from "./usage";

const TEXT = bilingual({
  en: {
    plans: {
      yourPlan: "Your plan",
      pick: "Pick a plan.",
      makeYours: "Make your video.",
      startToday: "Start today.",
      ending: (plan: string, day: string) =>
        `${plan} ends on ${day}. Choose a plan to keep making videos.`,
      changing: (plan: string, day: string, next: string) =>
        `${plan} until ${day}, then ${next}.`,
      current: (plan: string, day: string) =>
        `You're on ${plan}. Its credits renew on ${day}.`,
      managed:
        "Your plan is arranged with the Foco team. Extra credits are on Credits.",
      pitch: "Every plan has the same studio. Pick how many videos a month.",
      briefSaved: "Saved. It's ready to make right after checkout.",
      list: "Plans",
      for: {
        starter: "For a product or two",
        pro: "For weekly launches across products",
        studio: "For teams, agencies and catalogs",
      },
      ends: (day: string) => `Ends ${day}`,
      currentPlan: "Current plan",
      from: (day: string) => `From ${day}`,
      recommended: "Recommended",
      bestValue: "Best value",
      perMonth: "+ VAT / month",
      creditsMonth: (credits: string) => `${credits} credits a month`,
      firstMonth: (credits: string) => `+${credits} the first month`,
      firstMonthVideos: (videos: number) => `${videos} the first month`,
      estimate: "Estimated videos a month",
      withFoco: "with Foco",
      withLite: "with Foco Lite",
      opening: "Opening",
      keep: (plan: string) => `Keep ${plan}`,
      scheduled: "Scheduled",
      upgrade: (plan: string) => `Upgrade to ${plan}`,
      switch: (plan: string) => `Switch to ${plan}`,
      choose: (plan: string) => `Choose ${plan}`,
      note: "Spend credits your way: more videos with Foco Lite, more polish with Foco.",
      included: "In every plan",
      questions: "Questions",
      secure: "Secure payment by Stripe",
      soon: "Plans open when this studio launches",
      billing: "Credits and billing",
      signIn: "Sign in",
    },
    included: {
      directors: "Foco and Foco Lite directors",
      voices: "Ten studio voices",
      songs: "36 songs included",
      downloads: "Full-quality downloads",
      topup: (credits: string, price: string) =>
        `Extra credits anytime: ${credits} for ${price}`,
      change: "Change or cancel anytime",
    },
    faq: {
      credit: {
        q: "What does a credit pay for?",
        a: "The Director's work and everything it generates: voice, images, music and footage. Video estimates are for videos made in one go; changes you ask for use credits too. Foco Lite uses about a third of the credits Foco does, so every plan makes about three times the videos with it.",
      },
      runOut: {
        q: "What if I run out?",
        a: (credits: string, price: string, months: number) =>
          `Add ${credits} extra credits for ${price} whenever you need them; they last ${months} months while you have a plan. Or move up a plan and pay only the difference for the rest of the month.`,
      },
      carry: {
        q: "Do unused credits carry over?",
        a: "A plan's credits renew every month and start fresh; what's left doesn't carry over. Extra credits last a year.",
      },
      change: {
        q: "Can I change or cancel?",
        a: "Anytime, from Credits. A bigger plan starts right away with its extra credits; a smaller plan or a cancellation takes effect when the month ends.",
      },
    },
    checkout: {
      topupAdded: (credits: string) => `${credits} extra credits added.`,
      topupProcessing:
        "Your payment is processing. The credits arrive once it clears.",
      onPlan: (plan: string) => `You're on ${plan}`,
      processing: "Payment processing",
      notYet: "We couldn't confirm it yet",
      confirming: "Confirming your payment",
      ready: (credits: string, day: string, welcome: string) =>
        `${credits} credits ready${welcome ? ` + ${welcome} welcome credits` : ""} · renews ${day}`,
      pending:
        "Your plan starts as soon as the payment clears. We'll keep your brief.",
      wait: "One moment while Stripe confirms the payment.",
      make: "Make the video",
      start: "Start a video",
      close: "Close",
    },
  },
  es: {
    plans: {
      yourPlan: "Tu plan",
      pick: "Elige un plan.",
      makeYours: "Crea tu vídeo.",
      startToday: "Empieza hoy.",
      ending: (plan: string, day: string) =>
        `${plan} termina el ${day}. Elige un plan para seguir creando vídeos.`,
      changing: (plan: string, day: string, next: string) =>
        `${plan} hasta el ${day}; después, ${next}.`,
      current: (plan: string, day: string) =>
        `Tienes el plan ${plan}. Sus créditos se renuevan el ${day}.`,
      managed:
        "Tu plan está acordado con el equipo de Foco. Los créditos extra están en Créditos.",
      pitch:
        "Todos los planes tienen el mismo estudio. Elige cuántos vídeos al mes.",
      briefSaved: "Guardado. Podrás crearlo justo después del pago.",
      list: "Planes",
      for: {
        starter: "Para uno o dos productos",
        pro: "Para lanzamientos semanales de varios productos",
        studio: "Para equipos, agencias y catálogos",
      },
      ends: (day: string) => `Termina el ${day}`,
      currentPlan: "Plan actual",
      from: (day: string) => `Desde el ${day}`,
      recommended: "Recomendado",
      bestValue: "El más rentable",
      perMonth: "+ IVA / mes",
      creditsMonth: (credits: string) => `${credits} créditos al mes`,
      firstMonth: (credits: string) => `+${credits} el primer mes`,
      firstMonthVideos: (videos: number) => `${videos} el primer mes`,
      estimate: "Vídeos estimados al mes",
      withFoco: "con Foco",
      withLite: "con Foco Lite",
      opening: "Abriendo",
      keep: (plan: string) => `Mantener ${plan}`,
      scheduled: "Programado",
      upgrade: (plan: string) => `Subir a ${plan}`,
      switch: (plan: string) => `Cambiar a ${plan}`,
      choose: (plan: string) => `Elegir ${plan}`,
      note: "Gasta los créditos a tu manera: más vídeos con Foco Lite, más pulido con Foco.",
      included: "En todos los planes",
      questions: "Preguntas",
      secure: "Pago seguro con Stripe",
      soon: "Los planes se abren cuando se lance este estudio",
      billing: "Créditos y facturación",
      signIn: "Iniciar sesión",
    },
    included: {
      directors: "Directores Foco y Foco Lite",
      voices: "Diez voces de estudio",
      songs: "36 canciones incluidas",
      downloads: "Descargas en máxima calidad",
      topup: (credits: string, price: string) =>
        `Créditos extra cuando quieras: ${credits} por ${price}`,
      change: "Cambia o cancela cuando quieras",
    },
    faq: {
      credit: {
        q: "¿Qué se paga con los créditos?",
        a: "El trabajo del director y todo lo que genera: voz, imágenes, música y clips. Los vídeos estimados son vídeos hechos a la primera; los cambios que pidas también usan créditos. Foco Lite usa más o menos un tercio de los créditos que Foco, así que con él cualquier plan da para unas tres veces más vídeos.",
      },
      runOut: {
        q: "¿Y si me quedo sin créditos?",
        a: (credits: string, price: string, months: number) =>
          `Añade ${credits} créditos extra por ${price} cuando los necesites; duran ${months} meses mientras tengas un plan. O sube de plan y paga solo la diferencia por lo que queda de mes.`,
      },
      carry: {
        q: "¿Se acumulan los créditos que no uso?",
        a: "Los créditos del plan se renuevan cada mes y empiezan de cero; lo que sobra no se acumula. Los créditos extra duran un año.",
      },
      change: {
        q: "¿Puedo cambiar de plan o cancelarlo?",
        a: "Cuando quieras, desde Créditos. Un plan mayor empieza al momento con sus créditos extra; un plan menor o una cancelación se aplica al terminar el mes.",
      },
    },
    checkout: {
      topupAdded: (credits: string) => `${credits} créditos extra añadidos.`,
      topupProcessing:
        "Tu pago se está procesando. Los créditos llegarán cuando se confirme.",
      onPlan: (plan: string) => `Ya tienes el plan ${plan}`,
      processing: "Pago en proceso",
      notYet: "Aún no hemos podido confirmarlo",
      confirming: "Confirmando tu pago",
      ready: (credits: string, day: string, welcome: string) =>
        `${credits} créditos listos${welcome ? ` + ${welcome} de bienvenida` : ""} · se renueva el ${day}`,
      pending:
        "Tu plan empieza en cuanto se confirme el pago. Guardamos lo que has escrito.",
      wait: "Un momento mientras Stripe confirma el pago.",
      make: "Crear el vídeo",
      start: "Empezar un vídeo",
      close: "Cerrar",
    },
  },
});

/** A price in the visitor's locale: €29 in English, 29 € in Spanish. */
export const euro = (n: number) =>
  new Intl.NumberFormat(locale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n);
/** A Foco price, which excludes VAT: €29 + VAT, 29 € + IVA. */
export const exVat = (n: number) =>
  `${euro(n)} + ${lang() === "es" ? "IVA" : "VAT"}`;
/** A day in the visitor's locale: 6 Oct, 6 oct. */
export const day = (iso: string) =>
  new Date(iso).toLocaleDateString(locale(), {
    day: "numeric",
    month: "short",
  });
const credits = (n: number) => creditNumber(n, lang());

const INCLUDED = [
  [Clapperboard, "directors"],
  [AudioLines, "voices"],
  [Music2, "songs"],
  [Download, "downloads"],
  [Plus, "topup"],
  [Check, "change"],
] as const;
const QUESTIONS = ["credit", "runOut", "carry", "change"] as const;

/** The plans for a visitor who isn't signed in: choosing one starts with an account. */
export function PublicPlans({ config }: { config: StudioConfig }) {
  const language = useLang(),
    t = useText(TEXT).plans;
  // usage pings (src/usage.ts) while signed out
  useEffect(() => watchPage("/plans", language), [language]);
  return (
    <div className="public-plans">
      <header className="welcome-top">
        <Link to="/" aria-label="foco">
          <Logo />
        </Link>
        <LanguageSwitch />
        <Link className="button ghost" to="/entrar">
          {t.signIn}
        </Link>
      </header>
      <PlansScreen account={null} config={config} onError={() => {}} />
    </div>
  );
}

export function PlansScreen({
  account,
  config,
  onError,
}: {
  account: Account | null;
  config: StudioConfig;
  onError: (s: string) => void;
}) {
  const copy = useText(TEXT),
    t = copy.plans;
  const location = useLocation(),
    navigate = useNavigate();
  const [opening, setOpening] = useState<PlanId | null>(null);
  const sub = account?.subscription;
  // An account the studio team activated has no subscription to change here.
  const managed = account?.plan === "active" && !sub;
  // Before the first plan payment, each card shows its welcome credits.
  const welcome = (!account || account.welcome) && !sub && !managed;
  // A brief written before choosing a plan waits in the composer.
  const brief =
    account?.plan === "inactive"
      ? (location.state?.brief as string | undefined) || savedBrief(account.id)
      : "";
  const choose = (plan: PlanId, keep = false) => {
    if (!account) return navigate("/empezar");
    setOpening(plan);
    // Keeping a plan that is ending or changing is undone in Stripe's portal.
    void post(
      keep ? "/billing/portal" : sub ? "/billing/change" : "/billing/checkout",
      { plan },
    )
      .then(({ url }) => window.location.assign(url))
      .catch((e) => {
        onError(e.message);
        setOpening(null);
      });
  };
  return (
    <div className="page plans page-enter">
      <header className="plans-head">
        <h1 className="plans-title">
          {sub || managed ? (
            t.yourPlan
          ) : (
            <>
              {t.pick}{" "}
              <span className="split-text">
                {brief ? t.makeYours : t.startToday}
              </span>
            </>
          )}
        </h1>
        <p className="plans-sub">
          {sub
            ? sub.cancelling
              ? t.ending(PLANS[sub.tier].name, day(sub.renews))
              : sub.next
                ? t.changing(
                    PLANS[sub.tier].name,
                    day(sub.renews),
                    PLANS[sub.next].name,
                  )
                : t.current(PLANS[sub.tier].name, day(sub.renews))
            : managed
              ? t.managed
              : t.pitch}
        </p>
        {brief && (
          <div className="plans-brief">
            <FileText size={16} />
            <span className="plans-brief-text">“{brief.trim()}”</span>
            <span className="plans-brief-note">{t.briefSaved}</span>
          </div>
        )}
      </header>
      <ol className="plan-cards" aria-label={t.list}>
        {PLAN_IDS.map((id) => {
          const plan = PLANS[id],
            videos = videosFor(plan.credits, "foco"),
            bonus = welcome ? plan.welcome : 0,
            // The first month's count, when the welcome credits change it.
            first = (model: DirectorModel) => {
              const more = videosFor(plan.credits + bonus, model);
              return more > videosFor(plan.credits, model) ? (
                <span className="plan-bonus">
                  {" · "}
                  {t.firstMonthVideos(more)}
                </span>
              ) : null;
            },
            current = sub?.tier === id,
            keep = current && (sub!.cancelling || !!sub!.next),
            upcoming = sub?.next === id,
            featured = !sub && id === "pro",
            // The way up: the next plan above the customer's, or Pro.
            lead = sub
              ? !sub.cancelling &&
                !sub.next &&
                PLAN_IDS[PLAN_IDS.indexOf(sub.tier) + 1] === id
              : id === "pro";
          return (
            <li
              key={id}
              className={`plan-card ${featured ? "featured" : ""} ${current ? "current" : ""}`}
            >
              <div className="plan-top">
                <h2 className="plan-name">{plan.name}</h2>
                {current ? (
                  <span className="plan-flag mine">
                    {sub!.cancelling ? t.ends(day(sub!.renews)) : t.currentPlan}
                  </span>
                ) : upcoming ? (
                  <span className="plan-flag mine">
                    {t.from(day(sub!.renews))}
                  </span>
                ) : featured ? (
                  <span className="plan-flag">{t.recommended}</span>
                ) : id === "studio" && !sub ? (
                  <span className="plan-flag quiet">{t.bestValue}</span>
                ) : null}
              </div>
              <p className="plan-for">{t.for[id]}</p>
              <p className="plan-price">
                <b className="tnum">{euro(plan.price)}</b>
                <span>{t.perMonth}</span>
              </p>
              <p className="plan-credits">
                {t.creditsMonth(credits(plan.credits))}
                {bonus > 0 && (
                  <span className="plan-bonus">
                    {" · "}
                    {t.firstMonth(credits(bonus))}
                  </span>
                )}
              </p>
              <div className="plan-estimate">
                <span className="lbl">{t.estimate}</span>
                <p>
                  <Clapperboard size={15} />
                  <span>
                    <b className="tnum">{videos}</b> {t.withFoco}
                    {first("foco")}
                  </span>
                </p>
                <p>
                  <Feather size={15} />
                  <span>
                    <b className="tnum">{videosFor(plan.credits, "lite")}</b>{" "}
                    {t.withLite}
                    {first("lite")}
                  </span>
                </p>
              </div>
              <Button
                className={`plan-cta ${lead || keep ? "primary" : "ghost"} full`}
                disabled={
                  !config.billing ||
                  managed ||
                  (current && !keep) ||
                  upcoming ||
                  opening !== null
                }
                onClick={() => choose(id, keep)}
              >
                {opening === id ? (
                  <>
                    <LoaderCircle size={16} className="spin" /> {t.opening}
                  </>
                ) : keep ? (
                  t.keep(plan.name)
                ) : current ? (
                  t.yourPlan
                ) : upcoming ? (
                  t.scheduled
                ) : sub ? (
                  <>
                    {plan.price > PLANS[sub.tier].price
                      ? t.upgrade(plan.name)
                      : t.switch(plan.name)}{" "}
                    <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    {t.choose(plan.name)} <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </li>
          );
        })}
      </ol>
      <p className="plans-note">{t.note}</p>
      <div className="plans-more">
        <section className="plans-included" aria-labelledby="included-title">
          <h2 className="h2" id="included-title">
            {t.included}
          </h2>
          <ul>
            {INCLUDED.map(([Icon, key]) => (
              <li key={key}>
                <Icon size={17} />
                {key === "topup"
                  ? copy.included.topup(
                      credits(TOPUP.credits),
                      exVat(TOPUP.price),
                    )
                  : copy.included[key]}
              </li>
            ))}
          </ul>
        </section>
        <section className="plans-faq" aria-labelledby="faq-title">
          <h2 className="h2" id="faq-title">
            {t.questions}
          </h2>
          {QUESTIONS.map((key) => {
            const { q, a } = copy.faq[key];
            return (
              <details key={key}>
                <summary>
                  {q}
                  <ChevronDown size={16} />
                </summary>
                <p>
                  {typeof a === "string"
                    ? a
                    : a(
                        credits(TOPUP.credits),
                        exVat(TOPUP.price),
                        TOPUP.months,
                      )}
                </p>
              </details>
            );
          })}
        </section>
      </div>
      <p className="plans-trust">
        <Lock size={13} />
        {config.billing ? t.secure : t.soon}
        {sub && (
          <>
            {" · "}
            <Link to="/credits">{t.billing}</Link>
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Back from Stripe: the plan is confirmed with Stripe directly, then the
 * customer goes straight back to the brief they were writing.
 */
export function CheckoutReturn({
  account,
  session,
  onAccount,
  onNotice,
  onDone,
}: {
  account: Account;
  session: string;
  onAccount: (a: Account) => void;
  onNotice: (s: string) => void;
  onDone: () => void;
}) {
  const t = useText(TEXT).checkout;
  const navigate = useNavigate();
  const [state, setState] = useState<"confirming" | "ready" | "pending">(
    "confirming",
  );
  const [welcome, setWelcome] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    void post<{
      account: Account;
      kind: "plan" | "topup";
      paid: boolean;
      welcome: number;
    }>("/billing/confirm", { session })
      .then(({ account: next, kind, paid, welcome }) => {
        if (cancelled) return;
        setWelcome(welcome);
        onAccount(next);
        window.dispatchEvent(new Event("cutline:credits"));
        if (kind === "topup") {
          const said = text(TEXT).checkout;
          onNotice(
            paid
              ? said.topupAdded(credits(TOPUP.credits))
              : said.topupProcessing,
          );
          onDone();
        } else setState(next.plan === "active" ? "ready" : "pending");
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [session, onAccount, onNotice, onDone]);
  const sub = account.subscription;
  const brief = savedBrief(account.id);
  const title =
    state === "ready" && sub
      ? t.onPlan(PLANS[sub.tier].name)
      : state === "pending"
        ? t.processing
        : error
          ? t.notYet
          : t.confirming;
  return (
    <Modal title={title} onClose={onDone} className="welcome-plan">
      <div className={`wp-lens ${state === "ready" ? "in" : ""}`}>
        <Mark />
      </div>
      <h2 className="wp-title">{title}</h2>
      <p className="wp-sub">
        {state === "ready" && sub
          ? t.ready(
              credits(PLANS[sub.tier].credits),
              day(sub.renews),
              welcome ? credits(welcome) : "",
            )
          : state === "pending"
            ? t.pending
            : error || t.wait}
      </p>
      {state === "ready" && (
        <Button
          className="primary large full"
          autoFocus
          onClick={() => {
            onDone();
            navigate("/new", brief ? { state: { make: true } } : undefined);
          }}
        >
          {brief ? t.make : t.start}
          <ArrowRight size={17} />
        </Button>
      )}
      {state === "confirming" && !error && (
        <LoaderCircle size={20} className="spin wp-wait" />
      )}
      {(error || state === "pending") && (
        <Button className="ghost full" onClick={onDone}>
          {t.close}
        </Button>
      )}
    </Modal>
  );
}
